import { useMemo } from 'react';
import type { MyRegistration } from '@swiss-magic/shared-types';
import {
  useDashboardFilters,
  type DashboardFilterConfig,
  type DashboardFilters,
} from '@/lib/dashboard-filters';

// Story 080 — player dashboard sort & filter.
// Player-specific config for the generic dashboard-filter hook.

const STATUS_CHIPS = [
  'accepted',
  'pending',
  'rejected',
  'withdrawn',
  'following',
] as const;
const TIME_CHIPS = ['upcoming', 'in_progress', 'past'] as const;
const SORT_OPTIONS = ['default', 'name_asc', 'date_asc', 'date_desc'] as const;

function statusChipOf(r: MyRegistration): string {
  if (r.kind === 'follow') return 'following';
  switch (r.enrollmentStatus) {
    case 'accepted':
      return 'accepted';
    case 'none':
      return 'pending';
    case 'withdrawn':
      return 'withdrawn';
    case 'rejected':
    case 'blocked':
    case 'eliminated':
      return 'rejected';
    default:
      return 'pending';
  }
}

function timeChipsOf(r: MyRegistration): string[] {
  switch (r.tournamentStatus) {
    case 'in_progress':
      return ['in_progress'];
    case 'completed':
      return ['past'];
    default:
      return ['upcoming'];
  }
}

function compareDateAsc(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b);
}

function compareDateDesc(a: string | null, b: string | null): number {
  return -compareDateAsc(a, b);
}

// Lifecycle-bucket order for the player default sort. Mirrors the
// 'in_progress' / 'upcoming' / 'past' chips; past sinks last because
// it's hidden by default anyway.
const PLAYER_BUCKET_ORDER: Record<string, number> = {
  in_progress: 0,
  upcoming: 1,
  past: 2,
};

function bucketOf(r: MyRegistration): string {
  const chips = timeChipsOf(r);
  return chips[0] ?? 'upcoming';
}

function compareDefault(a: MyRegistration, b: MyRegistration): number {
  const ba = PLAYER_BUCKET_ORDER[bucketOf(a)] ?? 99;
  const bb = PLAYER_BUCKET_ORDER[bucketOf(b)] ?? 99;
  if (ba !== bb) return ba - bb;
  // Within bucket: upcoming + in_progress sort by start_date ASC
  // (what's coming up / been on longest), past sorts DESC (most
  // recently played first).
  const bucket = bucketOf(a);
  if (bucket === 'past') {
    return compareDateDesc(a.tournamentStartDate, b.tournamentStartDate);
  }
  return compareDateAsc(a.tournamentStartDate, b.tournamentStartDate);
}

const COMPARATORS: Record<string, (a: MyRegistration, b: MyRegistration) => number> = {
  default: compareDefault,
  name_asc: (a, b) => a.tournamentName.localeCompare(b.tournamentName),
  date_asc: (a, b) => compareDateAsc(a.tournamentStartDate, b.tournamentStartDate),
  date_desc: (a, b) => compareDateDesc(a.tournamentStartDate, b.tournamentStartDate),
};

export function usePlayerDashboardFilters(
  items: MyRegistration[],
): DashboardFilters<MyRegistration> {
  const config = useMemo<DashboardFilterConfig<MyRegistration>>(
    () => ({
      statusChips: STATUS_CHIPS,
      timeChips: TIME_CHIPS,
      // 'past' rows are hidden when no time chip is selected; clicking
      // the Past chip brings them back. Chip count badge always shows
      // the unfiltered total so the player knows what's tucked away.
      timeChipsHiddenByDefault: ['past'],
      sortOptions: SORT_OPTIONS,
      classifyStatus: statusChipOf,
      classifyTime: timeChipsOf,
      matchesSearch: (r, q) => r.tournamentName.toLowerCase().includes(q),
      comparators: COMPARATORS,
    }),
    [],
  );
  return useDashboardFilters(items, config);
}
